import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HomeIcon, ShoppingCartIcon, Upload, BarChart3, Menu } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, Ticket, HandHelping, Briefcase,
  CircleUserRound, LayoutTemplate, Mail, LogOut, X,
  Layers, Tag, Tags, Warehouse, Inbox, MessageSquare, Users, Zap,
  Hammer, UserCog, Wallet, Receipt, TrendingUp, LayoutDashboard,
  Settings, CreditCard, BookOpen,
} from 'lucide-react';
import axios from 'axios';
import Swal from 'sweetalert2';
import AuthContext from '../context/AuthContext';
import { usePluginContext } from '../context/PluginContext';
import API_BASE_URL from '../config/api';
import { devLog } from '../utils/devLog';

// Same short-lived cache the desktop Sidebar uses, so the two navs agree on
// subscription state without an extra request per page.
const SUB_CACHE_KEY = 'inikru_sub_status_cache';
const SUB_CACHE_TTL_MS = 60000;
const readSubCache = () => {
  try {
    const raw = sessionStorage.getItem(SUB_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - parsed.ts > SUB_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
};
const writeSubCache = (data) => {
  try {
    sessionStorage.setItem(SUB_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch { /* private mode */ }
};

/**
 * Mobile bottom navigation. Mirrors the SAME nav model as the desktop
 * Sidebar: identical destinations, plugin gating (hidden unless installed),
 * and the subscription gate on Upload. Previously it listed legacy routes
 * (Designer KYC / Designer Profile) the desktop nav had dropped, bypassed
 * the Upload gate, and couldn't reach any plugin page or the Plugin Store.
 */
const MobileBottomNav = ({ onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { authToken } = useContext(AuthContext) || {};
  const { isPluginEnabled } = usePluginContext();
  const [showMore, setShowMore] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(() => readSubCache() || { is_active: false });

  useEffect(() => {
    if (!authToken?.access) return;
    if (readSubCache()) return; // fresh enough
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/api/get_subscription_status/`, {
        headers: { Authorization: `Bearer ${authToken.access}` },
      })
      .then((res) => {
        if (cancelled) return;
        setSubscriptionStatus(res.data);
        writeSubCache(res.data);
      })
      .catch((err) => devLog.error('MobileBottomNav subscription fetch failed:', err));
    return () => { cancelled = true; };
  }, [authToken?.access]);

  const handleLockedUpload = () => {
    const trialUsed = !!subscriptionStatus.has_used_free_trial;
    Swal.fire({
      title: trialUsed ? 'Your free trial has ended' : 'Activate Your Free Trial',
      text: trialUsed
        ? 'Choose a plan to keep uploading products and selling on your store.'
        : 'Start your free 14-day trial to upload products and build your store. No credit card needed!',
      icon: 'info',
      confirmButtonText: trialUsed ? 'View Plans' : 'Start Free Trial',
      confirmButtonColor: '#B40A0A',
      showCancelButton: true,
      cancelButtonText: 'Later',
    }).then((result) => {
      if (result.isConfirmed) navigate('/subscription');
    });
  };

  const uploadLocked = !subscriptionStatus.is_active;

  const mainTabs = [
    { icon: HomeIcon, label: 'Store', to: '/homepage' },
    { icon: ShoppingCartIcon, label: 'Orders', to: '/Orders' },
    { icon: Upload, label: 'Upload', to: '/UploadProduct', locked: uploadLocked },
    { icon: BarChart3, label: 'Analytics', to: '/analytics' },
  ];

  // Grouped exactly like the desktop sidebar; plugin items hidden unless installed.
  const moreSections = [
    {
      title: 'Products',
      items: [
        isPluginEnabled('inventory') && { icon: Warehouse, label: 'Inventory', to: '/inventory' },
        { icon: Layers, label: 'Collections', to: '/collections-manager' },
        { icon: Tag, label: 'Categories', to: '/categories' },
        { icon: Tags, label: 'Catalog', to: '/catalog-settings' },
        { icon: Ticket, label: 'Coupons', to: '/my-coupons' },
      ],
    },
    {
      title: 'Orders',
      items: [
        { icon: BookOpen, label: 'Order Book', to: '/orderbook' },
        isPluginEnabled('unified_inbox') && { icon: Inbox, label: 'Unified Inbox', to: '/orders-unified' },
        isPluginEnabled('whatsapp') && { icon: MessageSquare, label: 'WhatsApp', to: '/whatsapp' },
        isPluginEnabled('bid_requests') && { icon: HandHelping, label: 'Bid Requests', to: '/Proposals' },
        isPluginEnabled('customers') && { icon: Users, label: 'Customers', to: '/customers' },
        isPluginEnabled('automations') && { icon: Zap, label: 'Automations', to: '/automations' },
      ],
    },
    {
      title: 'Business',
      items: [
        isPluginEnabled('daily_ops') && { icon: LayoutDashboard, label: 'Daily Ops', to: '/dashboard' },
        isPluginEnabled('email_marketing') && { icon: Mail, label: 'Email Marketing', to: '/email-marketing' },
        isPluginEnabled('production_board') && { icon: Hammer, label: 'Production', to: '/production' },
        isPluginEnabled('team') && { icon: UserCog, label: 'Team', to: '/karigars' },
        isPluginEnabled('payments') && { icon: Wallet, label: 'Payments', to: '/payments' },
        isPluginEnabled('expenses') && { icon: Receipt, label: 'Expenses', to: '/expenses' },
        isPluginEnabled('suppliers') && { icon: Briefcase, label: 'Suppliers', to: '/suppliers' },
        isPluginEnabled('report_pnl') && { icon: TrendingUp, label: 'P&L Report', to: '/reports/pnl' },
        isPluginEnabled('report_gstr1') && { icon: Receipt, label: 'GSTR-1', to: '/reports/gstr1' },
        isPluginEnabled('report_goals') && { icon: TrendingUp, label: 'Goals', to: '/reports/goals' },
      ],
    },
    {
      title: 'Store',
      items: [
        { icon: LayoutTemplate, label: 'Themes', to: '/TemplateThemes' },
        { icon: Globe, label: 'SEO', to: '/seo-settings' },
        // Plugin Store intentionally NOT in the nav (owner decision, 2026-07-11).
      ],
    },
    {
      title: 'Account',
      items: [
        { icon: CircleUserRound, label: 'Brand Profile', to: '/Profile' },
        { icon: CreditCard, label: 'Connect Payments', to: '/connect-payments' },
        { icon: BarChart3, label: 'Earnings', to: '/earnings' },
        { icon: Settings, label: 'Store Settings', to: '/store-settings' },
        { icon: Mail, label: 'Support', to: '/Contact' },
      ],
    },
  ]
    .map((section) => ({ ...section, items: section.items.filter(Boolean) }))
    .filter((section) => section.items.length > 0);

  const isActive = (to) => location.pathname === to;
  const isMoreActive = moreSections.some((s) => s.items.some((item) => location.pathname === item.to));

  return (
    <>
      {/* More Menu Bottom Sheet */}
      <AnimatePresence>
        {showMore && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 z-[9998]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMore(false)}
            />
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-[9999] bg-white rounded-t-[20px] shadow-2xl"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 bg-line rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-line">
                <h3 className="font-display text-lg font-extrabold tracking-tight text-ink">More</h3>
                <button
                  onClick={() => setShowMore(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 active:bg-gray-200"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Grouped items */}
              <div className="max-h-[60vh] overflow-y-auto px-4 pb-2">
                {moreSections.map((section) => (
                  <div key={section.title} className="pt-3">
                    <p className="px-1 font-display text-[10.5px] font-bold uppercase tracking-[0.14em] text-gray-500">
                      {section.title}
                    </p>
                    <div className="grid grid-cols-3 gap-1 pt-1">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.to);
                        return (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={() => setShowMore(false)}
                            className={`flex flex-col items-center gap-2 py-3 px-2 rounded-xl transition-all active:scale-95 ${
                              active
                                ? 'bg-orange-50 text-primary'
                                : 'text-gray-600 hover:bg-gray-50 active:bg-gray-100'
                            }`}
                          >
                            <div className={`w-11 h-11 rounded-[10px] flex items-center justify-center ${
                              active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500'
                            }`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <span className={`text-[11px] font-medium text-center leading-tight ${
                              active ? 'text-primary' : 'text-gray-600'
                            }`}>
                              {item.label}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Logout */}
              <div className="px-4 pb-6 pt-2 border-t border-line">
                <button
                  onClick={() => {
                    setShowMore(false);
                    if (onLogout) onLogout();
                  }}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-[10px] text-red-600 bg-red-50 active:bg-red-100 font-display font-bold text-sm transition-all"
                >
                  <LogOut className="w-4.5 h-4.5" />
                  Log out
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-[9997] bg-white border-t border-line lg:hidden safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-1">
          {mainTabs.map((tab) => {
            const Icon = tab.icon;
            const active = isActive(tab.to);
            return (
              <Link
                key={tab.to}
                to={tab.to}
                onClick={(e) => {
                  // Same gate as the desktop sidebar: locked Upload explains
                  // itself instead of dumping the user on a blocked page.
                  if (tab.locked) {
                    e.preventDefault();
                    handleLockedUpload();
                  }
                }}
                className={`flex flex-col items-center justify-center flex-1 h-full relative active:scale-95 transition-transform ${
                  tab.locked ? 'opacity-50' : ''
                }`}
              >
                {active && (
                  <motion.div
                    layoutId="bottomNavIndicator"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] bg-primary rounded-b-full"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
                <div className={`w-10 h-10 flex items-center justify-center rounded-[10px] transition-all ${
                  active ? 'bg-orange-50' : ''
                }`}>
                  <Icon
                    className={`w-[22px] h-[22px] transition-colors ${
                      active ? 'text-primary' : 'text-gray-400'
                    }`}
                    strokeWidth={active ? 2.2 : 1.8}
                  />
                </div>
                <span className={`text-[10px] mt-0.5 font-medium transition-colors ${
                  active ? 'text-primary' : 'text-gray-400'
                }`}>
                  {tab.label}
                </span>
              </Link>
            );
          })}

          {/* More button */}
          <button
            onClick={() => setShowMore(true)}
            className="flex flex-col items-center justify-center flex-1 h-full relative active:scale-95 transition-transform"
          >
            {isMoreActive && (
              <motion.div
                className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] bg-primary rounded-b-full"
              />
            )}
            <div className={`w-10 h-10 flex items-center justify-center rounded-[10px] transition-all ${
              isMoreActive ? 'bg-orange-50' : ''
            }`}>
              <Menu
                className={`w-[22px] h-[22px] transition-colors ${
                  isMoreActive ? 'text-primary' : 'text-gray-400'
                }`}
                strokeWidth={isMoreActive ? 2.2 : 1.8}
              />
            </div>
            <span className={`text-[10px] mt-0.5 font-medium transition-colors ${
              isMoreActive ? 'text-primary' : 'text-gray-400'
            }`}>
              More
            </span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
